import { useEffect, useMemo, useCallback, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useAppDispatch } from "../../store/hook";
import dashboardService from "../../services/dashboard";
import {
  OnboardingModal,
  MonthNavigation,
  KPICards,
  DRETable,
  FinancialEvolutionChart,
  DraggablePanel,
} from "../../components/Dashboard";

import { useDashboardOnboarding } from "../../hooks/useDashboardOnboarding";
import PanelShell from "../../components/Dashboard/PanelShell";
import { getMonthDateRange } from "../../utils/dateRange";
import type {
  DashboardFilter,
  KPIData,
  DREHierarchicalResponse,
  DREHierarchicalLine,
  DREHierarchicalNode,
  FinancialEvolution,
} from "../../types/dashboard";
import type { ExpandNodeParams } from "../../components/Dashboard/DRETable";

import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import { useGetStoredSelectedRegional, useGetStoredSelectedCategories, useGetStoredSelectedClients, useGetStoredSelectedContracts, useGetStoredSelectedVehicles } from "@store/volksCustomAssetTree/volksCustomAssetTreeSlice";
import { SelectedFilters } from "../../components";
import moment from "moment";
import { useDebounce } from "../../hooks/useDebounce";

type PanelType = "dre" | "chart";

interface Panel {
  id: PanelType;
  order: number;
}

const DEFAULT_PANELS: Panel[] = [
  { id: "dre", order: 0 },
  { id: "chart", order: 1 },
];

export default function DashboardScreen() {
  const dispatch = useAppDispatch();
  const [expandedDREItems, setExpandedDREItems] = useState<string[]>([]);

  const {
    onboardingStep,
    onboarding,
    handleCloseOnboarding,
    handleNextStep,
    handlePrevStep,
    handleFilialNameChange,
    handleNumFiliaisChange,
    onboardingLoading,
    onboardingSaving,
  } = useDashboardOnboarding();

  const startDate = useGetStoredStartDate();
  const endDate =  useGetStoredEndDate();

  const selectedRegional = useGetStoredSelectedRegional();
  const selectedCategories = useGetStoredSelectedCategories();
  const selectedClients = useGetStoredSelectedClients();
  const selectedContracts = useGetStoredSelectedContracts();
  const selectedVehicles = useGetStoredSelectedVehicles();


  const [panels, setPanels] = useState<Panel[]>(DEFAULT_PANELS);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => new Date());
  const [kpi, setKpi] = useState<KPIData | null>(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [dre, setDre] = useState<DREHierarchicalResponse | null>(null);
  const [dreLoading, setDreLoading] = useState(true);
  const [dreLoadingNodes, setDreLoadingNodes] = useState<string[]>([]);
  const [financialEvolution, setFinancialEvolution] = useState<FinancialEvolution[] | null>(null);
  const [financialEvolutionLoading, setFinancialEvolutionLoading] = useState(true);

  const monthRange = useMemo(() => getMonthDateRange(selectedMonth), [selectedMonth]);

  const requestFilters = useMemo<DashboardFilter>(() => {

    const useCustomRange = Boolean(startDate && endDate);
    const payload: DashboardFilter = {
      startDate: useCustomRange ? startDate : monthRange.startDate,
      endDate: useCustomRange ? endDate : monthRange.endDate,
    };

    if (selectedRegional?.length) payload.regional = selectedRegional;
    if (selectedCategories?.length) payload.categoryIds = selectedCategories;
    if (selectedClients?.length) payload.clientIds = selectedClients;
    if (selectedContracts?.length) payload.contracts = selectedContracts;
    if (selectedVehicles?.length) payload.vehicleIds = selectedVehicles;

    return payload;
  }, [monthRange, startDate, endDate, selectedRegional, selectedCategories, selectedClients, selectedContracts, selectedVehicles]);

  const debouncedFilters = useDebounce(requestFilters, 500);

  const fetchKPI = async () => {

    setKpiLoading(true);

    try {
      const data = await dashboardService.getKPI(debouncedFilters);
      setKpi(data);
    } catch (error) {
      console.error("Error fetching KPI:", error);
    } finally {
      setKpiLoading(false);
    }
  }

  const fetchDRE =  async () => {

    setDreLoading(true);

    try {
      const data = await dashboardService.getVolksTotalDRE(debouncedFilters);
      setDre(data);
    } catch (error) {
      console.error("Error fetching DRE data:", error);
    } finally {
      setDreLoading(false);
    }
  }

  const fetchFinancialEvolution = async () => {

    setFinancialEvolutionLoading(true);

    try {
      const data = await dashboardService.getFinancialEvolution(debouncedFilters);
      setFinancialEvolution(data);
    } catch (error) {
      console.error("Error fetching financial evolution data:", error);
    } finally {
      setFinancialEvolutionLoading(false);
    }
  }

  useEffect(() => {
    fetchKPI();
  }, [debouncedFilters]);

  useEffect(() => {
    fetchDRE();
  }, [debouncedFilters]);

  useEffect(() => {
    fetchFinancialEvolution();
  }, [debouncedFilters]);

  const periodKey = useMemo(() => {
    const start = requestFilters.startDate ?? "";
    const end = requestFilters.endDate ?? "";
    return `${start}_${end}`;
  }, [requestFilters.startDate, requestFilters.endDate]);

  useEffect(() => {
    setExpandedDREItems([]);
  }, [periodKey]);

  const currentMonth = useMemo(
    () => selectedMonth.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }).toUpperCase(),
    [selectedMonth]
  );

  const goToPrevMonth = useCallback(() => {
    if (startDate && endDate) {
      const currentStart = moment(startDate, "YYYY-MM-DD");
      const newStart = currentStart.clone().subtract(1, "month").startOf("month");
      const newEnd = newStart.clone().endOf("month");

      dispatch(setStartDate(newStart.format("YYYY-MM-DD")));
      dispatch(setEndDate(newEnd.format("YYYY-MM-DD")));
    } else {
      setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  }, [startDate, endDate, dispatch]);

  const goToNextMonth = useCallback(() => {
    if (startDate && endDate) {

      const currentStart = moment(startDate, "YYYY-MM-DD");
      const newStart = currentStart.clone().add(1, "month").startOf("month");
      const newEnd = newStart.clone().endOf("month");

      dispatch(setStartDate(newStart.format("YYYY-MM-DD")));
      dispatch(setEndDate(newEnd.format("YYYY-MM-DD")));
    } else {
      setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  }, [startDate, endDate, dispatch]);

  const toggleDRE = (id: string) => {
    setExpandedDREItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const updateNodeChildren = (
    lines: DREHierarchicalLine[],
    nodeId: string,
    children: DREHierarchicalNode[]
  ): DREHierarchicalLine[] => {
    const newLines = lines.map((line) => {
      if (line.children) {
        const updatedChildren = updateNodeInChildren(line.children, nodeId, children);
        const wasUpdated = updatedChildren.some((child, idx) => child !== line.children![idx]);
        if (wasUpdated) {
          return { ...line, children: updatedChildren };
        }
      }
      return line;
    });
    return newLines;
  };

  const updateNodeInChildren = (
    nodes: DREHierarchicalNode[],
    nodeId: string,
    newChildren: DREHierarchicalNode[]
  ): DREHierarchicalNode[] => {
    return nodes.map((node) => {
      const currentNodeId = node.compositeId ?? node.id;
      if (currentNodeId === nodeId) {
        return { ...node, children: newChildren };
      }
      if (node.children && node.children.length > 0) {
        const updatedChildren = updateNodeInChildren(node.children, nodeId, newChildren);
        const wasUpdated = updatedChildren.some((child, idx) => child !== node.children![idx]);
        if (wasUpdated) {
          return { ...node, children: updatedChildren };
        }
      }
      return node;
    });
  };

  const handleExpandDRENode = useCallback(async (params: ExpandNodeParams) => {
    const { nodeId, compositeId, nodeType, lineType, parentPath } = params;

    setDreLoadingNodes((prev) => [...prev, compositeId]);

    try {
      const response = await dashboardService.expandDRENode(requestFilters, {
        nodeId,
        nodeType,
        lineType,
        parentPath,
      });

      if (response.children && response.children.length > 0) {
        setDre((prevDre) => {
          if (!prevDre) {
            return prevDre;
          }
          const newLines = updateNodeChildren(prevDre.lines, compositeId, response.children);
          return {
            ...prevDre,
            lines: newLines,
          };
        });
      }
    } catch (error) {
      console.error("Error expanding DRE node:", error);
    } finally {
      setDreLoadingNodes((prev) => prev.filter((id) => id !== compositeId));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestFilters]);

  const movePanel = useCallback((dragIndex: number, hoverIndex: number) => {
    setPanels((prevPanels) => {
      const newPanels = [...prevPanels];
      const [draggedPanel] = newPanels.splice(dragIndex, 1);
      newPanels.splice(hoverIndex, 0, draggedPanel);
      return newPanels.map((panel, index) => ({ ...panel, order: index }));
    });
  }, []);

  const renderPanel = (panelId: PanelType) => {
    switch (panelId) {
      case "dre":
        return (
          <PanelShell loading={dreLoading} loadingLabel="DRE" hasData={Boolean(dre?.lines?.length)}>
            <DRETable
              key={periodKey}
              data={dre?.lines ?? []}
              expandedItems={expandedDREItems}
              loadingNodes={dreLoadingNodes}
              requestFilters={requestFilters}
              onToggle={toggleDRE}
              onExpandNode={handleExpandDRENode}
            />
          </PanelShell>
        );
      // case "chart":
      //   return (
      //     <PanelShell
      //       loading={financialEvolutionLoading}
      //       loadingLabel="Evolução financeira"
      //       hasData={Boolean(financialEvolution && financialEvolution.length)}
      //     >
      //       <FinancialEvolutionChart data={financialEvolution ?? []} />
      //     </PanelShell>
      //   );
      default:
        return null;
    }
  };

  if (onboardingLoading) {
    return (
      <div className="width-100pct height-100pct display-flex justify-content-center align-items-center bg-lighest padding-25">
        <PanelShell loading loadingLabel="" hasData={false}>
          <></>
        </PanelShell>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="width-100pct height-100pct display-flex flex-column bg-lighest">
        <OnboardingModal
          show={false}
          step={onboardingStep}
          onNext={handleNextStep}
          onPrev={handlePrevStep}
          isSaving={onboardingSaving}
          onClose={handleCloseOnboarding}
          numFiliais={onboarding.numFiliais}
          filiaisNames={onboarding.filiaisNames}
          onNumFiliaisChange={handleNumFiliaisChange}
          onFilialNameChange={handleFilialNameChange}
        />

        <div className="flex-1 overflow-y-auto">
          <div className="display-flex flex-column gap-20 padding-25 bg-light">
            <MonthNavigation
              endDate={endDate}
              startDate={startDate}
              currentMonth={currentMonth}
              onPrevMonth={goToPrevMonth}
              onNextMonth={goToNextMonth}
              onStartDateChange={(date) => dispatch(setStartDate(date))}
              onEndDateChange={(date) => dispatch(setEndDate(date))}
              showDateRangePicker={showDateRangePicker}
              onClick={() => setShowDateRangePicker(!showDateRangePicker)}
              onCloseDateRangePicker={() => setShowDateRangePicker(false)}
            />

            <SelectedFilters requestFilters={requestFilters} />

          </div>

          <div className="padding-25">
            <PanelShell loading={kpiLoading} loadingLabel="KPIs" hasData={Boolean(kpi)} className="margin-bottom-20">
              {kpi && <KPICards kpi={kpi} />}
            </PanelShell>

            <>
              {panels
                .sort((a, b) => a.order - b.order)
                .map((panel, index) => (
                  <div key={panel.id} className="margin-bottom-20">
                    <DraggablePanel id={panel.id} index={index} movePanel={movePanel}>
                      {renderPanel(panel.id)}
                    </DraggablePanel>
                  </div>
                ))}
            </>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
